import React, { useState, useContext, useEffect, useCallback } from "react";
import { Modal, Button, Form, Table } from "react-bootstrap";
import { debounce } from "lodash";
import { SettingsContext } from "client_config/lite/AccountSettingsButton"; // Make sure this path matches your context file

export default function AddUser(props) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [foundUsers, setFoundUsers] = useState([]);
  const [newUser, setNewUser] = useState({
    first_name: '',
    last_name: '',
    email: '',
    role: props.userType == "employee" ? "Employee" : "Client",
    department: 'None',
  });

  const context = useContext(SettingsContext);

  // Add Existing User Functionality (Left Side)
  // Use useCallback to memoize the debounced function
  const debouncedSearch = useCallback(debounce((filter) => {
    if (!filter.trim()) {
      setFoundUsers([]);
      return;
    }
    fetch(`/datapulse/user-access/search-users/${filter}?` + new URLSearchParams({
      userType: props.userType,
    }))
    .then((response) => response.json())
    .then((data) => setFoundUsers(data));
  }, 750), [props.userType]); // Re-create this function only if userType changes

  useEffect(() => {
    // Call the debounced function within useEffect
    debouncedSearch(searchTerm);

    // Cleanup function to cancel the debounced call if component unmounts or input changes
    return () => debouncedSearch.cancel();
  }, [searchTerm, debouncedSearch]);

  const showModal = () => setIsModalVisible(true);
  const hideModal = () => setIsModalVisible(false);

  const handleSearchChange = (event) => {
    event.preventDefault();
    setSearchTerm(event.target.value);
  };

  const handleAddUser = (user) => {
    if (props.userType === "client") {
      if (!context.clientUsers.some((u) => u.email === user.email)) {
        context.setClientUsers((prevUsers) => [...prevUsers, user]);
      }
    } else {
      if (!context.teamUsers.some((u) => u.email === user.email)) {
        context.setTeamUsers((prevUsers) => [...prevUsers, user]);
      }
    }
    hideModal();
  };

  // Add New User Functionality (Right Side)
  const handleNewUserChange = (event) => {
    event.preventDefault();
    const { name, value } = event.target;
    setNewUser({ ...newUser, [name]: value });
  };

  const createNewUser = (event) => {
    event.preventDefault();
    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    let hasError = false;
    const userToAdd = { ...newUser, userType: props.userType };
    const firstName = userToAdd.first_name.trim();
    const lastName = userToAdd.last_name.trim();
    const email = userToAdd.email.trim();

    if (!firstName) {
      hasError = true;
      document.getElementById("new-user-first-name").classList.add("lite-invalid-input");
    } else {
      document.getElementById("new-user-first-name").classList.remove("lite-invalid-input");
    }
    if (!lastName) {
      hasError = true;
      document.getElementById("new-user-last-name").classList.add("lite-invalid-input");
    } else {
      document.getElementById("new-user-last-name").classList.remove("lite-invalid-input");
    }
    if (!isValidEmail(email)) {
      hasError = true;
      document.getElementById("new-user-email").classList.add("lite-invalid-input");
    } else {
      document.getElementById("new-user-email").classList.remove("lite-invalid-input");
    }

    if (!hasError) {
      if (props.userType === "client") {
        if (!context.clientUsers.some((u) => u.email === userToAdd.email)) {
          context.setClientUsers((prevUsers) => [...prevUsers, userToAdd]);
        }
      } else {
        if (!context.teamUsers.some((u) => u.email === userToAdd.email)) {
          context.setTeamUsers((prevUsers) => [...prevUsers, userToAdd]);
        }
      }
      hideModal();
    }
  };

  return (
    <>
      <button type="button" className="button b-type-primary b-size-small b-color-green" onClick={showModal} style={{alignSelf: "baseline"}}>
        + Add User
      </button>
      <Modal className="lite-modal-2" show={isModalVisible} onHide={hideModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            <h4>Add a new {props.userType} user</h4>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-4">Add {props.userType} user to this account. You may search for an existing user or create a new one.</p>
          <div className="d-flex add-user-modal-body">
            <div className="flex-fill add-user-modal-section add-user-left-side">
              <h5 className="f20 text-left">Search</h5>
              <label className="input-label" htmlFor="user-search">Begin typing a user's name or email</label>
              <input
                id="user-search"
                type="text"
                placeholder="Search users"
                onChange={handleSearchChange}
                className="mb-3 lite-input"
              />
              <div className="user-search-table">
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {foundUsers.map((user) => (
                      <tr key={user.email}>
                        <td>{`${user.first_name} ${user.last_name}`}</td>
                        <td>{user.email}</td>
                        <td>
                          <button type="button" className="button b-type-primary b-size-small b-color-green" onClick={() => handleAddUser(user)}>
                            <i className="fa fa-plus" aria-hidden="true"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
            <div className="flex-fill add-user-modal-section add-user-right-side">
              <h5 className="f20 text-left">Create</h5>
              <div className="new-user-form d-flex" style={{ gap: props.userType == "employee" ? "24px" : "0px" }}>
                <div className="basic-information">
                  <div className="lite-form-group mb-3">
                    <label className="input-label" htmlFor="new-user-first-name">First Name</label>
                    <input className="lite-input" type="text" id="new-user-first-name" name="first_name" value={newUser.firstName} onChange={handleNewUserChange} />
                  </div>
                  <div className="lite-form-group mb-3">
                    <label className="input-label" htmlFor="new-user-last-name">Last Name</label>
                    <input className="lite-input" type="text" id="new-user-last-name" name="last_name" value={newUser.lastName} onChange={handleNewUserChange} />
                  </div>
                  <div className="lite-form-group mb-3">
                    <label className="input-label" htmlFor="new-user-email">Email Address</label>
                    <input className="lite-input" type="email" id="new-user-email" name="email" value={newUser.email} onChange={handleNewUserChange} />
                  </div>
                </div>
                {props.userType === "employee" && (
                  <div className="employee-fields">
                    <>
                      <div className="lite-form-group mb-3">
                        <label className="input-label" htmlFor="new-user-level">Employee Level</label>
                        <div className="select-wrapper">
                          <select className="lite-select" id="new-user-level" name="role" value={newUser.level} onChange={handleNewUserChange}>
                            <option value="Employee" default>Restricted</option>
                            <option value="Specialist">Specialist</option>
                            <option value="Manager">Manager</option>
                            <option value="Admin">Admin</option>
                          </select>
                        </div>
                      </div>
                      <div className="lite-form-group mb-3">
                        <label className="input-label" htmlFor="new-user-department">Employee Department</label>
                        <div className="select-wrapper">
                          <select className="lite-select" id="new-user-department" name="department" value={newUser.department} onChange={handleNewUserChange}>
                            <option value="None" default>None</option>
                            <option value="PPC">PPC</option>
                            <option value="SEO">SEO</option>
                            <option value="Both">Both</option>
                            <option value="DEV">DEV</option>
                          </select>
                        </div>
                      </div>
                    </>
                  </div>
                )}
              </div>
              <button className="button b-type-primary b-size-small b-color-green mt-3" onClick={(e) => createNewUser(e)}>Create User</button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}